import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { ListScreen, TableSkeleton } from '../components/chrome/list-screen'
import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Button } from '../components/ui/button'
import { Drawer } from '../components/ui/dialog'
import { Blank, Panel } from '../components/ui/panel'
import { Table, TBody, Td, Th, THead, Tr } from '../components/ui/table'
import { Chip } from '../components/ui/tone'
import { api, type AuditActionCount, type AuditEntry, type Page } from '../lib/api'
import { useSession } from '../lib/session'

/**
 * The audit trail (§57).
 *
 * The screen that answers "who did that, and what did it look like before". Every other screen
 * shows the current state of something; this one is the only place the *change* survives.
 *
 * Two design decisions worth keeping:
 *
 *  - **The row shows the change, not just the action.** §57's own examples are "changed mailbox
 *    limit 10 → 20" and "removed suppression" — a list of verbs with no values is a list nobody
 *    can act on, and the before/after is already in the row.
 *  - **There is no delete, and no edit.** Not because it is hard, but because §57 requires audit
 *    logs not to be editable through the dashboard. The API has no such route either, so this is
 *    not a UI-only restraint.
 */

/**
 * Actions are recorded as dotted machine names. These are the ones the code actually writes today,
 * grepped from every `audit.record(` call — not a guess at what a log might contain.
 */
const ACTION_LABEL: Record<string, string> = {
  'auth.login': 'Signed in',
  'auth.login.failed': 'Sign-in failed',
  'campaign.created': 'Created a campaign',
  'campaign.dry_run.disable': 'Left dry run',
  'campaign.dry_run.enable': 'Entered dry run',
  'contacts.import': 'Imported contacts',
  'domain.activate': 'Activated a domain',
  'domain.dns.auto_publish': 'Published DNS records',
  'domain.register': 'Registered a domain',
  'mailbox.limits.changed': 'Changed mailbox limits',
  'organization.outbound.disable': 'Stopped this organization sending',
  'organization.outbound.enable': 'Allowed this organization to send',
  'reply.reclassified': 'Reclassified a reply',
  'suppression.add': 'Suppressed an address',
  'suppression.remove': 'Removed a suppression',
  'template.version.approved': 'Approved a template version',
  'template.version.created': 'Added a template version',
}

/**
 * Campaign transitions are recorded as `campaign.transition.<state>`, one action name per state,
 * so they cannot be listed above without listing all eleven. Handled by prefix instead.
 */
const TRANSITION = 'campaign.transition.'

/** Actions worth a coloured chip. Everything else stays grey — colour only where it matters. */
function toneFor(action: string): 'bad' | 'warn' | 'neutral' {
  // Leaving dry run and un-suppressing are the two acts on this list that can put mail in front
  // of somebody who should not receive it. They are the reason §57 exists.
  if (action === 'campaign.dry_run.disable') return 'bad'
  if (action === 'suppression.remove') return 'bad'
  if (action === 'auth.login.failed') return 'warn'
  if (action.startsWith('organization.outbound.')) return 'warn'
  if (action === TRANSITION + 'failed' || action === TRANSITION + 'cancelled') return 'warn'
  return 'neutral'
}

function label(action: string) {
  if (ACTION_LABEL[action]) return ACTION_LABEL[action]
  if (action.startsWith(TRANSITION)) {
    const state = action.slice(TRANSITION.length).replace(/_/g, ' ')
    return `Campaign → ${state}`
  }
  // Falls back to the machine name rather than to a friendly guess. An action this file has not
  // been taught is a real action somebody recorded, and hiding it behind "Other" would lose it.
  return action
}

/**
 * A one-line summary of what actually changed, built from the recorded values. Falls back to the
 * object when there is nothing to diff — a creation has no old value and that is not a defect.
 */
function summarise(entry: AuditEntry): string {
  const before = entry.oldValue as Record<string, unknown> | null
  const after = entry.newValue as Record<string, unknown> | null

  if (before && after) {
    const changed = Object.keys(after).filter(
      (key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]),
    )
    if (changed.length > 0) {
      return changed
        .map((key) => `${key} ${format(before[key])} → ${format(after[key])}`)
        .join(', ')
    }
  }

  if (after && Object.keys(after).length > 0) {
    return Object.entries(after)
      .map(([key, value]) => `${key} ${format(value)}`)
      .join(', ')
  }

  return entry.objectId ? `${entry.objectType} ${entry.objectId.slice(0, 8)}` : entry.objectType
}

function format(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'on' : 'off'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function AuditScreen() {
  const { can } = useSession()
  const [search, setSearch] = useState('')
  const [action, setAction] = useState<string | undefined>(undefined)
  const [open, setOpen] = useState<AuditEntry | null>(null)

  const entries = useQuery({
    queryKey: ['audit', action],
    queryFn: () =>
      api.get<Page<AuditEntry>>(
        `/audit?take=200${action ? `&action=${encodeURIComponent(action)}` : ''}`,
      ),
    enabled: can('view_audit_log'),
  })

  const actions = useQuery({
    queryKey: ['audit', 'actions'],
    queryFn: () => api.get<AuditActionCount[]>('/audit/actions'),
    enabled: can('view_audit_log'),
  })

  if (!can('view_audit_log')) {
    return (
      <Screen>
        <ScreenHeader
          title="Audit log"
          description="Every sensitive act, with who did it and what changed."
        />
        <Panel>
          <Blank
            icon="denied"
            title="You do not have access to the audit log"
            body="Reading it requires the view_audit_log permission. Ask an administrator of your organization."
          />
        </Panel>
      </Screen>
    )
  }

  const rows = (entries.data?.items ?? []).filter((entry) => {
    if (!search) return true
    const needle = search.toLowerCase()
    return (
      entry.action.toLowerCase().includes(needle) ||
      label(entry.action).toLowerCase().includes(needle) ||
      (entry.user?.email ?? '').toLowerCase().includes(needle) ||
      entry.objectType.toLowerCase().includes(needle)
    )
  })

  // Built from what was recorded, not from a hardcoded list — a filter naming actions that never
  // happen is a filter that teaches the wrong thing about the system.
  const tabs = [
    { id: 'all', label: 'Everything', count: entries.data?.total },
    ...(actions.data ?? [])
      .slice()
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)
      .map((row) => ({ id: row.action, label: label(row.action), count: row.count })),
  ]

  return (
    <>
      <ListScreen
        title="Audit log"
        subtitle="Every sensitive act, with who did it, what it was before and what it is now. Append-only — nothing here can be edited or deleted, by design."
        query={search}
        onQuery={setSearch}
        queryPlaceholder="Filter by action, person or object"
        tabs={tabs}
        activeTab={action ?? 'all'}
        onTab={(id) => setAction(id === 'all' ? undefined : id)}
        hint={
          entries.data && entries.data.total > entries.data.items.length
            ? `Showing the ${entries.data.items.length} most recent of ${entries.data.total.toLocaleString()}.`
            : undefined
        }
      >
        {entries.isLoading ? (
          <TableSkeleton columns={5} />
        ) : entries.isError ? (
          <Blank
            icon="error"
            tone="bad"
            title="The audit log could not be read"
            action={<Button onClick={() => void entries.refetch()}>Try again</Button>}
          />
        ) : rows.length > 0 ? (
          <Table>
            <THead>
              <Th width={150}>When</Th>
              <Th width={190}>Action</Th>
              <Th>What changed</Th>
              <Th width={190}>Who</Th>
            </THead>
            <TBody>
              {rows.map((entry) => (
                <Tr key={entry.id} onOpen={() => setOpen(entry)}>
                  <Td className="text-muted">{new Date(entry.createdAt).toLocaleString()}</Td>
                  <Td>
                    <Chip tone={toneFor(entry.action)}>{label(entry.action)}</Chip>
                  </Td>
                  <Td className="text-muted">{summarise(entry)}</Td>
                  <Td className="text-muted">
                    {/* Null is a real answer here — a failed login has no user, and saying
                        "system" would be a lie about who tried. */}
                    {entry.user ? entry.user.email : <span className="text-faint">not signed in</span>}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        ) : search || action ? (
          <Blank
            icon="noResults"
            title="Nothing matches that filter."
            action={
              <Button
                onClick={() => {
                  setSearch('')
                  setAction(undefined)
                }}
              >
                Clear filter
              </Button>
            }
          />
        ) : (
          <Blank
            icon="audit"
            title="Nothing has been recorded yet."
            body="Signing in, changing the sending switch, removing a suppression and every campaign transition all land here."
          />
        )}
      </ListScreen>

      <Drawer
        open={open !== null}
        title={open ? label(open.action) : ''}
        onClose={() => setOpen(null)}
      >
        {open && <EntryDetail entry={open} />}
      </Drawer>
    </>
  )
}

function EntryDetail({ entry }: { entry: AuditEntry }) {
  return (
    <div className="space-y-5 text-[13px]">
      <dl className="grid grid-cols-[120px_1fr] gap-y-2.5">
        <dt className="text-muted">When</dt>
        <dd>{new Date(entry.createdAt).toLocaleString()}</dd>

        <dt className="text-muted">Action</dt>
        <dd className="font-mono text-[12.5px]">{entry.action}</dd>

        <dt className="text-muted">Object</dt>
        <dd>
          {entry.objectType}
          {entry.objectId && <span className="text-faint font-mono"> {entry.objectId}</span>}
        </dd>

        <dt className="text-muted">Who</dt>
        <dd>{entry.user ? `${entry.user.name} · ${entry.user.email}` : 'not signed in'}</dd>

        {entry.ipAddress && (
          <>
            <dt className="text-muted">From</dt>
            <dd className="font-mono text-[12.5px]">{entry.ipAddress}</dd>
          </>
        )}

        {entry.reason && (
          <>
            <dt className="text-muted">Reason</dt>
            <dd className="text-pretty">{entry.reason}</dd>
          </>
        )}
      </dl>

      {/* Both values, raw. The summary in the list is a convenience; this is the record, and a
          record that has been prettied up is one somebody will eventually mistrust. */}
      <div className="grid gap-4 sm:grid-cols-2">
        <ValueBlock label="Before" value={entry.oldValue} />
        <ValueBlock label="After" value={entry.newValue} />
      </div>
    </div>
  )
}

function ValueBlock({ label: name, value }: { label: string; value: unknown }) {
  return (
    <div>
      <p className="text-muted mb-1.5 text-[12px]">{name}</p>
      <pre className="bg-sel text-text overflow-x-auto rounded-[7px] p-3 font-mono text-[12px] leading-[1.55]">
        {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  )
}
